// src/components/TopBar/TopBar.tsx
import React, { useEffect, useRef, useState } from 'react';
import styles from './TopBar.module.css';
import { useBackground } from '../../contexts/BackgroundContext';
import { saveLocalBackgroundPath, uploadBackground } from '../Background/backgrounds';

export type ExcelFileType = {
  id: number;
  file_name?: string;
  chairman_id?: number;
  chairman_name?: string;
  upload_date?: string;
};

interface SearchResult {
  chairmanId: number;
  chairmanName: string;
  fileName: string;
}

interface TopBarProps {
  excelFiles?: ExcelFileType[];
  onSelectChairman?: (chairmanId: number, fileName?: string) => void;
}

const DEFAULT_TEXT_COLOR = '#1f2937';
const DEFAULT_SIDEBAR_TEXT_COLOR = '#1f2937';
const DEFAULT_SURFACE_OPACITY = '0.75';
const DEFAULT_TEXT_OPACITY = '1';
const DEFAULT_SURFACE_COLOR = '#ffffff';
const DEFAULT_SIDEBAR_SURFACE_COLOR = '#f1f5f9';

const TopBar: React.FC<TopBarProps> = ({ excelFiles = [], onSelectChairman = () => {} }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const { setMainImage, setMainVideo, setSideImage, setSideVideo, uiPreferences, setUiPreferences } = useBackground();
  const [showAppearance, setShowAppearance] = useState(false);
  const [fontColor, setFontColor] = useState(uiPreferences.textColor || DEFAULT_TEXT_COLOR);
  const [sidebarTextColor, setSidebarTextColor] = useState(uiPreferences.sidebarTextColor || DEFAULT_SIDEBAR_TEXT_COLOR);
  const [textOpacity, setTextOpacity] = useState(uiPreferences.textOpacity || DEFAULT_TEXT_OPACITY);
  const [surfaceOpacity, setSurfaceOpacity] = useState(uiPreferences.surfaceOpacity || DEFAULT_SURFACE_OPACITY);
  const [surfaceColor, setSurfaceColor] = useState(uiPreferences.surfaceColor || DEFAULT_SURFACE_COLOR);
  const [sidebarSurfaceColor, setSidebarSurfaceColor] = useState(uiPreferences.sidebarSurfaceColor || DEFAULT_SIDEBAR_SURFACE_COLOR);

  useEffect(() => {
    setFontColor(uiPreferences.textColor || DEFAULT_TEXT_COLOR);
    setSidebarTextColor(uiPreferences.sidebarTextColor || DEFAULT_SIDEBAR_TEXT_COLOR);
    setTextOpacity(uiPreferences.textOpacity || DEFAULT_TEXT_OPACITY);
    setSurfaceOpacity(uiPreferences.surfaceOpacity || DEFAULT_SURFACE_OPACITY);
    setSurfaceColor(uiPreferences.surfaceColor || DEFAULT_SURFACE_COLOR);
    setSidebarSurfaceColor(uiPreferences.sidebarSurfaceColor || DEFAULT_SIDEBAR_SURFACE_COLOR);
  }, [uiPreferences]);

  const mainFileRef = useRef<HTMLInputElement>(null);
  const sideFileRef = useRef<HTMLInputElement>(null);

  const inferTypeByExt = (url: string | null): 'image' | 'video' | null => {
    if (!url) return null;
    const u = url.split('?')[0].toLowerCase();
    const imgExt = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.avif', '.bmp', '.svg'];
    const vidExt = ['.mp4', '.webm', '.ogg', '.mov', '.m4v', '.avi', '.mkv'];
    if (imgExt.some((ext) => u.endsWith(ext))) return 'image';
    if (vidExt.some((ext) => u.endsWith(ext))) return 'video';
    return null;
  };

  // Фон загружает BackgroundProvider (контекст) — свой fetch /api/backgrounds/me/
  // в TopBar убран как дубль (раньше запрос уходил дважды: контекст + топбар).

  const handleSearch = (value: string) => {
    setQuery(value);
    if (!value.trim()) {
      setResults([]);
      return;
    }

    const q = value.toLowerCase();

    // ВАЖНО: пропускаем элементы без file_name / chairman_id
    const matches = excelFiles.filter((f) => (f.file_name || '').toLowerCase().includes(q) && typeof f.chairman_id === 'number');

    setResults(
      matches.map((f) => ({
        chairmanId: f.chairman_id as number,
        chairmanName: f.chairman_name || `ID: ${f.chairman_id}`,
        fileName: f.file_name || '',
      }))
    );
  };

  const promptAndPick = (target: 'main' | 'side') => {
    const msg =
      target === 'main'
        ? 'Рекомендации для основного фона:\n• Изображение: не ниже 1920×1080\n• Видео: 1920×1080 или 1280×720\n\nНажмите OK, чтобы выбрать файл.'
        : 'Рекомендации для фона сайдбара:\n• Изображение: ширина ≈ 280px, высота ≥ 1080px\n• Видео: вертикальное 1080×1920\n\nНажмите OK, чтобы выбрать файл.';

    if (!window.confirm(msg)) {
      return;
    }

    if (window.desktopApp) {
      onPickDesktop(target);
      return;
    }

    (target === 'main' ? mainFileRef : sideFileRef).current?.click();
  };

  const applyBackground = (target: 'main' | 'side', rawUrl: string) => {
    const url = rawUrl.startsWith('file://') ? rawUrl : `file://${rawUrl}`;
    const type = inferTypeByExt(url);

    if (!type) {
      alert('Поддерживаются изображения и видео.');
      return;
    }

    if (target === 'main') {
      if (type === 'image') setMainImage(url);
      if (type === 'video') setMainVideo(url);
      return;
    }

    if (type === 'image') setSideImage(url);
    if (type === 'video') setSideVideo(url);
  };

  const onPick = (target: 'main' | 'side') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    try {
      setLoading(true);
      const payload = await uploadBackground(target, f);

      if (target === 'main') {
        if (f.type.startsWith('image/')) setMainImage(payload.mainUrl || '');
        else if (f.type.startsWith('video/')) setMainVideo(payload.mainUrl || '');
        else alert('Поддерживаются изображения и видео.');
      } else {
        if (f.type.startsWith('image/')) setSideImage(payload.sideUrl || '');
        else if (f.type.startsWith('video/')) setSideVideo(payload.sideUrl || '');
        else alert('Поддерживаются изображения и видео.');
      }
    } catch (err) {
      console.error(err);
      alert('Не удалось загрузить фон');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const onPickDesktop = async (target: 'main' | 'side') => {
    if (!window.desktopApp) {
      return;
    }

    try {
      setLoading(true);
      const selectedPath = await window.desktopApp.pickBackground();
      if (!selectedPath) {
        return;
      }

      await saveLocalBackgroundPath(target, selectedPath);
      applyBackground(target, selectedPath);
    } catch (err) {
      console.error(err);
      alert('Не удалось выбрать фон');
    } finally {
      setLoading(false);
    }
  };

  const saveAppearance = async () => {
    await setUiPreferences({
      textColor: fontColor,
      sidebarTextColor,
      textOpacity,
      surfaceOpacity,
      surfaceColor,
      sidebarSurfaceColor,
    });
    setShowAppearance(false);
  };

  const resetAppearance = async () => {
    setFontColor(DEFAULT_TEXT_COLOR);
    setSidebarTextColor(DEFAULT_SIDEBAR_TEXT_COLOR);
    setTextOpacity(DEFAULT_TEXT_OPACITY);
    setSurfaceOpacity(DEFAULT_SURFACE_OPACITY);
    setSurfaceColor(DEFAULT_SURFACE_COLOR);
    setSidebarSurfaceColor(DEFAULT_SIDEBAR_SURFACE_COLOR);
    await setUiPreferences({
      textColor: DEFAULT_TEXT_COLOR,
      sidebarTextColor: DEFAULT_SIDEBAR_TEXT_COLOR,
      textOpacity: DEFAULT_TEXT_OPACITY,
      surfaceOpacity: DEFAULT_SURFACE_OPACITY,
      surfaceColor: DEFAULT_SURFACE_COLOR,
      sidebarSurfaceColor: DEFAULT_SIDEBAR_SURFACE_COLOR,
    });
    setShowAppearance(false);
  };

  return (
    <header className={styles.navbar}>
      <div className={styles.search}>
        <input
          type="text"
          placeholder="Search Excel..."
          className={styles.searchInput}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          disabled={loading}
        />

        {results.length > 0 && (
          <ul className={styles.searchResults}>
            {results.map((r, idx) => (
              <li
                key={idx}
                onClick={() => {
                  onSelectChairman(r.chairmanId, r.fileName);
                  setQuery('');
                  setResults([]);
                }}
              >
                {r.chairmanName} — {r.fileName}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.bgControls}>
        <div className={styles.group}>
          <button className={styles.btn} onClick={() => promptAndPick('main')} disabled={loading}>
            Установить фон (основной)
          </button>
          <input ref={mainFileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={onPick('main')} />
        </div>

        <div className={styles.group}>
          <button className={styles.btn} onClick={() => promptAndPick('side')} disabled={loading}>
            Установить фон (сайдбар)
          </button>
          <input ref={sideFileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={onPick('side')} />
        </div>
        <div className={styles.group}>
          <button className={styles.btn} onClick={() => setShowAppearance((v) => !v)} disabled={loading}>
            Настроить интерфейс
          </button>
        </div>
      </div>
      {showAppearance && (
        <div className={styles.appearancePanel}>
          <label>
            Цвет шрифта (основной)
            <input type="color" value={fontColor} onChange={(e) => setFontColor(e.target.value)} />
          </label>
          <label>
            Цвет шрифта (сайдбар)
            <input type="color" value={sidebarTextColor} onChange={(e) => setSidebarTextColor(e.target.value)} />
          </label>
          <label>
            Прозрачность шрифта (0.45-1)
            <input
              type="number"
              min="0.45"
              max="1"
              step="0.05"
              value={textOpacity}
              onChange={(e) => setTextOpacity(e.target.value)}
            />
          </label>
          <label>
            Цвет блоков (основной)
            <input type="color" value={surfaceColor} onChange={(e) => setSurfaceColor(e.target.value)} />
          </label>
          <label>
            Цвет блоков (сайдбар)
            <input type="color" value={sidebarSurfaceColor} onChange={(e) => setSidebarSurfaceColor(e.target.value)} />
          </label>
          <label>
            Прозрачность панелей (0.3-1)
            <input
              type="number"
              min="0.3"
              max="1"
              step="0.05"
              value={surfaceOpacity}
              onChange={(e) => setSurfaceOpacity(e.target.value)}
            />
          </label>
          <button className={styles.btn} onClick={saveAppearance} disabled={loading}>Сохранить стиль</button>
          <button className={styles.btn} onClick={resetAppearance} disabled={loading}>Сделать по умолчанию</button>
        </div>
      )}
    </header>
  );
};

export default TopBar;
